import "core-js/modules/es.symbol.js";
import "core-js/modules/es.symbol.description.js";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.number.constructor.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.string.trim.js";
import React from 'react';
import { useTranslation } from 'react-i18next';
import FormField, { DefaultFormFieldLayout } from 'components/FormField';
import FormStack from 'components/FormStack';
import Icon from 'components/Icon';
import Text from 'components/Text';
import TextLink from 'components/TextLink';
import TextInput from 'components/TextInput';
import TextArea from 'components/TextArea';
import { ImageField, TagField, AssetTypeField, LanguageField, CompletionTimeField, formatTags } from 'components/FormFields';
import TitleCard from 'components/TitleCard';
import Box from 'components/Box';
import Hint from 'components/Hint';
import { ContentDetailsInfo } from 'components/ContentInfo';
import { TYPE_ASSET_THUMBNAIL } from 'utils/file';
import config from 'utils/config';
import getContentItemType from 'utils/asset/getContentItemType';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function useDescriptionShortHint(_ref) {
  var description = _ref.description,
    aiSuggestion = _ref.aiSuggestion;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  if (!aiSuggestion) {
    return null;
  }
  if (aiSuggestion.status === 'pending') {
    return /*#__PURE__*/_jsxs(Text, {
      size: "xs",
      children: [/*#__PURE__*/_jsx(Icon, {
        vAlign: true,
        name: "sparkles",
        mr: "xxs"
      }), t('ai_summary_generating')]
    });
  } else if (aiSuggestion.status === 'error') {
    return /*#__PURE__*/_jsxs(Text, {
      size: "xs",
      children: [/*#__PURE__*/_jsx(Icon, {
        vAlign: true,
        name: "sparkles",
        mr: "xxs"
      }), t('ai_summary_error')]
    });
  } else if (aiSuggestion.status === 'ready') {
    return /*#__PURE__*/_jsxs(Text, {
      size: "xs",
      children: [/*#__PURE__*/_jsx(Icon, {
        vAlign: true,
        name: "sparkles",
        mr: "xxs"
      }), t('ai_summary_available'), ' ', /*#__PURE__*/_jsx(TextLink, {
        bold: true,
        as: "button",
        onClick: function onClick() {
          return description.set("".concat(description.value, " ").concat(aiSuggestion.value).trim());
        },
        children: t('actions_insert')
      })]
    });
  }
}
function GeneralDetailsCard(_ref2) {
  var isDistributed = _ref2.isDistributed,
    isEditMode = _ref2.isEditMode,
    fields = _ref2.fields,
    content = _ref2.content,
    aiSuggestions = _ref2.aiSuggestions,
    initialAssetDetails = _ref2.initialAssetDetails;
  var _useTranslation2 = useTranslation(),
    t = _useTranslation2.t;
  var name = fields.name,
    description = fields.description,
    tags = fields.tags,
    customType = fields.customType,
    locale = fields.locale,
    completionTime = fields.completionTime,
    thumbnailId = fields.thumbnailId;
  var contentId = content === null || content === void 0 ? void 0 : content.id;
  var descriptionHintContent = useDescriptionShortHint({
    description: description,
    aiSuggestion: aiSuggestions === null || aiSuggestions === void 0 ? void 0 : aiSuggestions.description
  });
  return /*#__PURE__*/_jsx(TitleCard, {
    variant: "raised",
    title: t('menu_general_settings'),
    sideContent: initialAssetDetails && /*#__PURE__*/_jsx(ContentDetailsInfo, {
      content: initialAssetDetails,
      type: "asset"
    }),
    children: /*#__PURE__*/_jsx(DefaultFormFieldLayout, {
      horizontal: true,
      children: /*#__PURE__*/_jsxs(FormStack, {
        spacing: "m",
        children: [isEditMode && isDistributed && /*#__PURE__*/_jsx(Box, {
          children: isDistributed && /*#__PURE__*/_jsx(Hint, {
            icon: "share",
            children: t('immutability_note_distributed_item_fields')
          })
        }), /*#__PURE__*/_jsx(FormField, {
          required: true,
          label: "".concat(t('form_title'), " *"),
          alert: name.wasChanged ? name.error : undefined,
          children: /*#__PURE__*/_jsx(TextInput, {
            value: name.value,
            onChange: function onChange(e) {
              return name.set(e.target.value);
            },
            disabled: isDistributed
          })
        }), /*#__PURE__*/_jsx(ImageField, {
          id: "assetCoverImage",
          thumbnailId: thumbnailId.value,
          onChange: function onChange(e) {
            return thumbnailId.set(e);
          },
          isDisabled: isDistributed,
          assetType: customType.value,
          contentType: TYPE_ASSET_THUMBNAIL,
          contentId: contentId,
          contentName: name.value,
          contentDescription: description.value
        }), /*#__PURE__*/_jsx(FormField, {
          label: t('form_description'),
          shortHint: descriptionHintContent,
          children: /*#__PURE__*/_jsx(TextArea, {
            maxRows: 8,
            minRows: 3,
            value: description.value,
            onChange: function onChange(e) {
              return description.set(e.target.value);
            },
            disabled: isDistributed
          })
        }), /*#__PURE__*/_jsx(TagField
        // Format tags to have all required information for correctly displaying the tag pills
        , {
          selectedTags: formatTags(tags.value),
          onChangeSelectedTags: function onChangeSelectedTags(data) {
            tags.set(data.selectedItems.map(function (tag) {
              return {
                name: tag.name,
                id: tag.id || ''
              };
            }));
          },
          isDisabled: isDistributed
        }), /*#__PURE__*/_jsx(AssetTypeField, {
          type: customType.value,
          onChange: function onChange(newType) {
            return customType.set(newType);
          },
          originalType: getContentItemType(content || {}, false),
          isDisabled: isDistributed
        }), config.assetLanguageEnabled && /*#__PURE__*/_jsx(LanguageField, {
          value: locale.value,
          onChange: function onChange(newLocale) {
            return locale.set(newLocale);
          },
          isDisabled: isDistributed
        }), /*#__PURE__*/_jsx(CompletionTimeField, {
          completionTime: Number(completionTime.value),
          onChange: function onChange(e) {
            return completionTime.set(Number(e.target.value));
          },
          isDisabled: isDistributed
        })]
      })
    })
  });
}
export default GeneralDetailsCard;